/* eslint-disable no-nested-ternary */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { Chart, Selection } from '../../../../../common/components/Chart';
import FlexContainer from '../../../../../common/components/atoms/FlexContainer';
import AddNoteChartOverlay from './AddNoteChartOverlay';
import OverlayTraceLegend from './OverlayTraceLegend';
import TagChartOutlierModal from './TagChartOutlierModal';
import {
  TrendChartContainer,
  Hidden,
  AbsoluteLoading,
  OverlayLoadingMessage
} from './TagChart.styled';

// quefrency arrives in ms rounded to 3 decimals; show all of it
const CEPSTRUM_X_DECIMAL_POINTS = 3;

const TagChartView = ({
  title,
  type,
  isCepstrum,
  showOverlay,
  filterSettings,
  selection,
  toFixedDecimalPoints,
  shaftSpeed,
  initialXunit,
  annotationsVisible,
  children,
  loading,
  showOverlayLoadingOverlay,
  loadingMessage,
  chartProps,
  chartData,
  chartColors,
  addingNote,
  skipOverlay,
  onAddNote,
  showOutlierModal,
  dataPos,
  dataPoint,
  onCloseOutlierModal,
  markReadingAsOutlier,
  deleteOutlierReading,
  isTraceOverlay,
  overlayLegendProps
}) => (
  <Fragment>
    {selection &&
      type === 'waveform' && !isCepstrum && !showOverlay && (
        <Selection
          data={{
            ...selection,
            x: selection.x / 1000,
            xUnit: 's'
          }}
          filterSettings={filterSettings}
        />
    )}
    {selection &&
      type !== 'trend' &&
      type !== 'demod_trend' &&
      type !== 'feature' &&
      (type !== 'waveform' || isCepstrum) && (
        <Selection
          isCepstrum={isCepstrum}
          xDecimalPoints={isCepstrum ? CEPSTRUM_X_DECIMAL_POINTS : undefined}
          toFixedDecimalPoints={toFixedDecimalPoints}
          shaftSpeed={shaftSpeed}
          initialXunit={initialXunit}
          data={selection}
          filterSettings={filterSettings}
          annotationsVisible={annotationsVisible}
        />
    )}
    {selection &&
      type === 'feature' && (
        <Hidden>
          <Selection data={selection} annotationsVisible={annotationsVisible} />
        </Hidden>
    )}
    <FlexContainer>
      <TrendChartContainer optionsVisible={!!children}>
        {(loading || showOverlayLoadingOverlay) && (
          <AbsoluteLoading>
            {showOverlayLoadingOverlay && (
              <OverlayLoadingMessage>
                {loadingMessage}
              </OverlayLoadingMessage>
            )}
          </AbsoluteLoading>
        )}
        <Chart
          {...chartProps}
          data={chartData}
          colors={chartColors}
          activeData={chartProps.activeData || chartData.map(() => true)}
        />
        {addingNote && !skipOverlay && <AddNoteChartOverlay onClick={onAddNote} text={title} />}
        <TagChartOutlierModal
          visible={showOutlierModal}
          dataPos={dataPos}
          dataPoint={dataPoint}
          onClose={onCloseOutlierModal}
          markReadingAsOutlier={markReadingAsOutlier}
          deleteOutlierReading={deleteOutlierReading}
        />
      </TrendChartContainer>
      {children}
    </FlexContainer>

    {(isTraceOverlay || chartData.length > 1) && (
      <OverlayTraceLegend {...overlayLegendProps} />
    )}
  </Fragment>
);

TagChartView.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  isCepstrum: PropTypes.bool,
  showOverlay: PropTypes.bool,
  filterSettings: PropTypes.object,
  selection: PropTypes.object,
  toFixedDecimalPoints: PropTypes.number.isRequired,
  shaftSpeed: PropTypes.number,
  initialXunit: PropTypes.string,
  annotationsVisible: PropTypes.bool,
  children: PropTypes.node,
  loading: PropTypes.bool,
  showOverlayLoadingOverlay: PropTypes.bool,
  loadingMessage: PropTypes.string,
  chartProps: PropTypes.object.isRequired,
  chartData: PropTypes.array.isRequired,
  chartColors: PropTypes.array.isRequired,
  addingNote: PropTypes.bool,
  skipOverlay: PropTypes.bool,
  onAddNote: PropTypes.func.isRequired,
  showOutlierModal: PropTypes.bool,
  dataPos: PropTypes.object.isRequired,
  dataPoint: PropTypes.object,
  onCloseOutlierModal: PropTypes.func.isRequired,
  markReadingAsOutlier: PropTypes.func.isRequired,
  deleteOutlierReading: PropTypes.func.isRequired,
  isTraceOverlay: PropTypes.bool,
  overlayLegendProps: PropTypes.object.isRequired
};

TagChartView.defaultProps = {
  isCepstrum: false,
  showOverlay: false,
  filterSettings: undefined,
  selection: null,
  shaftSpeed: null,
  initialXunit: null,
  annotationsVisible: false,
  children: null,
  loading: false,
  showOverlayLoadingOverlay: false,
  loadingMessage: '',
  addingNote: false,
  skipOverlay: false,
  showOutlierModal: false,
  dataPoint: null,
  isTraceOverlay: false
};

export default TagChartView;
